import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Brain, Target, Layers, Sparkles, LineChart } from "lucide-react";
import useEmblaCarousel from 'embla-carousel-react';
import StarCanvas from './StarCanvas';

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  gradient: string;
}

const features: Feature[] = [
  {
    icon: <Brain className="w-8 sm:w-12 h-8 sm:h-12" />,
    title: "Never Research Leads Again",
    description:
      "Our AI continuously analyzes your Ideal Customer Profile, automatically identifying and qualifying the perfect prospects across multiple data sources.",
    gradient: "from-purple-600 to-fuchsia-600",
  },
  {
    icon: <Target className="w-8 sm:w-12 h-8 sm:h-12" />,
    title: "Deep Insights, Zero Effort",
    description:
      "While you sleep, our AI researches each prospect, gathering insights from social media, news, and company data to create personalized outreach angles.",
    gradient: "from-violet-600 to-purple-600",
  },
  {
    icon: <Layers className="w-8 sm:w-12 h-8 sm:h-12" />,
    title: "Personalization at Scale",
    description:
      "Transform cold outreach into warm conversations with AI-driven personalization that makes every prospect feel like you've done hours of research.",
    gradient: "from-indigo-600 to-violet-600",
  },
  {
    icon: <Sparkles className="w-8 sm:w-12 h-8 sm:h-12" />,
    title: "Automated Engagement",
    description:
      "Keep conversations going with AI-driven follow-ups and responses that maintain the perfect balance of persistence and professionalism.",
    gradient: "from-purple-600 to-pink-600",
  },
  {
    icon: <LineChart className="w-8 sm:w-12 h-8 sm:h-12" />,
    title: "Performance Analytics",
    description:
      "Track your success with detailed insights and optimization tips that help you continuously improve your outreach effectiveness.",
    gradient: "from-fuchsia-600 to-purple-600",
  },
];

const BackgroundEffects = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Animated stars */}
    {[...Array(20)].map((_, i) => (
      <motion.div
        key={`star-${i}`}
        className="absolute text-white text-2xl"
        initial={{ opacity: 0.2, rotate: 0 }}
        animate={{
          opacity: [0.2, 0.8, 0.2],
          rotate: 360,
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 5 + Math.random() * 3,
          repeat: Infinity,
          delay: i * 0.2,
          ease: "easeInOut",
        }}
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
        }}
      >
        ✧
      </motion.div>
    ))}

    {/* Gradient overlay */}
    <div className="absolute inset-0 bg-gradient-radial from-purple-900/20 via-transparent to-transparent" />
  </div>
);

const StarField = ({ density = 100, speed = 1 }: { density?: number; speed?: number }) => {
  return (
    <div className="absolute inset-0 w-full h-full pointer-events-none">
      {[...Array(density)].map((_, i) => {
        const size = Math.random() * 1.5 + 0.5;
        const opacity = Math.random() * 0.5 + 0.2;
        const left = Math.random() * 100;
        const top = Math.random() * 100;
        const duration = (Math.random() * 2 + 3) / speed;
        
        return (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              width: size,
              height: size,
              left: `${left}%`,
              top: `${top}%`,
              opacity: 0
            }}
            animate={{
              opacity: [0, opacity, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: duration,
              repeat: Infinity,
              delay: Math.random() * -10,
              ease: "linear"
            }}
          />
        );
      })}
    </div>
  );
};

// Add keyframes animation to the root styles
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes twinkle {
    0%, 100% { opacity: 0; transform: scale(0); }
    50% { opacity: var(--opacity, 1); transform: scale(1); }
  }
`;
document.head.appendChild(styleSheet);

const FeatureCard = ({
  feature,
  isActive,
  offset,
}: {
  feature: Feature;
  isActive: boolean;
  offset: number;
}) => {
  // Hide cards that are not in view
  if (Math.abs(offset) > 1) return null;

  // Calculate position and transforms
  const xOffset = offset * 380;
  const scale = isActive ? 1 : 0.85;
  const opacity = isActive ? 1 : 0.5;
  const rotateY = offset * 15;
  const blur = Math.abs(offset) * 4;

  return (
    <motion.div
      className={cn(
        "absolute top-1/2 left-1/2",
        "w-[400px] h-[400px] rounded-2xl",
        "flex flex-col items-center justify-center text-center",
        isActive ? "cursor-default" : "cursor-pointer"
      )}
      style={{
        transformStyle: "preserve-3d",
        transformOrigin: "center center",
        perspective: "2000px",
        zIndex: isActive ? 10 : 5,
      }}
      initial={false}
      animate={{ 
        x: `calc(${xOffset}px - 50%)`,
        y: "-50%",
        scale,
        opacity,
        rotateY,
        filter: `blur(${blur}px) brightness(${isActive ? 1 : 0.7})`,
      }}
      transition={{
        duration: 0.7,
        ease: [0.32, 0.72, 0, 1]
      }}
    >
      {/* Card background with gradient */}
      <motion.div 
        className={cn(
          "absolute inset-0 rounded-2xl overflow-hidden",
          "bg-gradient-to-br",
          isActive ? feature.gradient : "from-gray-800 to-gray-900"
        )}
        initial={false}
        animate={{
          opacity: isActive ? 1 : 0.7
        }}
        transition={{ duration: 0.4 }}
      >
        <motion.div 
          className={cn(
            "absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent"
          )}
          initial={false}
          animate={{
            opacity: isActive ? 0.5 : 0.2
          }}
          transition={{ duration: 0.4 }}
        />
      </motion.div>

      {/* Content */}
      <motion.div 
        className="relative z-10 p-8 flex flex-col items-center"
        style={{
          transform: "translateZ(8px)"
        }}
        initial={false}
      >
        <motion.div 
          className="mb-8"
          initial={false}
          animate={{
            scale: isActive ? 1.1 : 1,
            y: isActive ? -5 : 0
          }}
          transition={{
            duration: 0.4,
            ease: "easeOut"
          }}
        >
          <motion.div 
            className={cn(
              "w-20 h-20 rounded-xl p-4",
              "backdrop-blur-xl border",
              "flex items-center justify-center",
              "transition-all duration-300",
              isActive 
                ? "bg-white/15 border-white/30"
                : "bg-white/10 border-white/20"
            )}
            initial={false}
          >
            {feature.icon}
          </motion.div>
        </motion.div>
        
        <motion.h3 
          className="text-2xl font-semibold text-white mb-4"
          initial={false}
          animate={{
            opacity: isActive ? 1 : 0.8
          }}
          transition={{ duration: 0.3 }}
        >
          {feature.title}
        </motion.h3>
        
        <motion.p 
          className={cn(
            "text-lg leading-relaxed",
            isActive ? "text-white/90" : "text-white/70"
          )}
          initial={false}
          animate={{
            opacity: isActive ? 1 : 0.7
          }}
          transition={{ duration: 0.3 }}
        >
          {feature.description}
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

// Mobile Card Component
const MobileFeatureCard = ({ 
  feature, 
  isActive, 
  index, 
  totalSlides,
  currentIndex 
}: { 
  feature: Feature; 
  isActive: boolean;
  index: number;
  totalSlides: number;
  currentIndex: number;
}) => {
  const offset = index - currentIndex;
  const scale = isActive ? 1 : 0.9;
  const opacity = isActive ? 1 : 0.5;
  const translateY = isActive ? 0 : 20;

  return (
    <motion.div
      className={cn(
        "w-full rounded-2xl min-h-[340px]",
        "flex flex-col items-center justify-center text-center",
        "relative backdrop-blur-lg",
        isActive ? "cursor-default" : "cursor-pointer"
      )}
      initial={{ scale: 0.9, opacity: 0, y: 20 }}
      animate={{ 
        scale,
        opacity,
        y: translateY,
        zIndex: isActive ? 10 : 5,
      }}
      transition={{
        type: "spring",
        stiffness: 100,
        damping: 15,
        mass: 0.8,
      }}
    >
      {/* Animated gradient background */}
      <div className={cn(
        "absolute inset-[1px] rounded-2xl overflow-hidden",
        "before:absolute before:inset-0 before:rounded-2xl",
        "before:bg-gradient-to-b before:from-white/30 before:to-transparent",
        isActive ? "before:opacity-60" : "before:opacity-30"
      )}>
        <motion.div 
          className={cn(
            "absolute inset-0 bg-gradient-to-br",
            isActive ? feature.gradient : "from-gray-700 to-gray-900"
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        />
        
        <motion.div 
          className={cn(
            "absolute inset-0 bg-gradient-to-br",
            isActive 
              ? "from-white/30"
              : "from-white/10",
            "via-transparent to-transparent"
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* Content with staggered animations */}
      <motion.div 
        className="relative z-10 p-5 flex flex-col items-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <motion.div 
          className="mb-4"
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ 
            type: "spring",
            stiffness: 200,
            damping: 15
          }}
        >
          <div className={cn(
            "w-14 h-14 rounded-xl p-3 backdrop-blur-xl border flex items-center justify-center",
            "transform transition-all duration-300",
            isActive 
              ? "bg-white/15 border-white/30 scale-110"
              : "bg-white/5 border-white/10"
          )}>
            {feature.icon}
          </div>
        </motion.div>
        
        <motion.h3 
          className="text-lg font-semibold text-white mb-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {feature.title}
        </motion.h3>
        
        <motion.p 
          className={cn(
            "text-sm leading-relaxed mb-4",
            isActive ? "text-white/90" : "text-white/60"
          )}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {feature.description}
        </motion.p>
        
        {/* Animated Pills/Badges */}
        <motion.div 
          className="flex flex-wrap gap-2 justify-center mt-auto"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <motion.span 
            className={cn(
              "px-3 py-1 rounded-full text-xs font-medium",
              "transform transition-all duration-300",
              isActive 
                ? "bg-purple-500/30 text-purple-200 scale-105"
                : "bg-white/5 text-white/40"
            )}
            whileHover={{ scale: 1.05 }}
          >
            AI Powered
          </motion.span>
          <motion.span 
            className={cn(
              "px-3 py-1 rounded-full text-xs font-medium",
              "transform transition-all duration-300",
              isActive 
                ? "bg-purple-500/30 text-purple-200 scale-105"
                : "bg-white/5 text-white/40"
            )}
            whileHover={{ scale: 1.05 }}
          >
            Automation
          </motion.span>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export const FeatureCards = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    dragFree: false,
    containScroll: 'trimSnaps'
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setCurrentSlide(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (!isAutoPlaying || isMobile) return;
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, isMobile]);

  // Mobile Layout
  if (isMobile) {
    return (
      <motion.section 
        className="relative py-12 overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Animated background grid */}
        <motion.div 
          className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:14px_24px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.3 }}
          transition={{ duration: 1 }}
        />
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.h2
            className="text-4xl font-serif text-center mb-8 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/80"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            You'll Get
          </motion.h2>

          <motion.div 
            className="overflow-hidden -mx-4"
            ref={emblaRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex-[0_0_100%] min-w-0 px-4"
                >
                  <MobileFeatureCard
                    feature={feature}
                    isActive={currentSlide === index}
                    index={index}
                    totalSlides={features.length}
                    currentIndex={currentSlide}
                  />
                </div>
              ))}
            </div>
          </motion.div>

          {/* Enhanced Navigation Controls */}
          <motion.div 
            className="flex flex-col items-center gap-4 mt-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            {/* Animated Dots */}
            <div className="flex justify-center gap-2">
              {features.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => emblaApi?.scrollTo(index)}
                  className={cn(
                    "w-1.5 h-1.5 rounded-full transition-all duration-500",
                    currentSlide === index
                      ? "w-8 bg-gradient-to-r from-purple-500 to-fuchsia-500"
                      : "bg-white/20 hover:bg-white/40"
                  )}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 * index }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Enhanced Arrow Buttons */}
            <div className="flex justify-center gap-6">
              <motion.button
                onClick={() => emblaApi?.scrollPrev()}
                className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-xl border border-white/20 flex items-center justify-center transition-all duration-300 group"
                whileHover={{ scale: 1.1, backgroundColor: "rgba(0,0,0,0.7)" }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                aria-label="Previous slide"
              >
                <motion.svg 
                  className="w-5 h-5 text-white/50 group-hover:text-white/90 transition-colors"
                  fill="none" 
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  whileHover={{ x: -2 }}
                >
                  <path
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </motion.svg>
              </motion.button>

              <motion.button
                onClick={() => emblaApi?.scrollNext()}
                className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-xl border border-white/20 flex items-center justify-center transition-all duration-300 group"
                whileHover={{ scale: 1.1, backgroundColor: "rgba(0,0,0,0.7)" }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                aria-label="Next slide"
              >
                <motion.svg 
                  className="w-5 h-5 text-white/50 group-hover:text-white/90 transition-colors"
                  fill="none" 
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  whileHover={{ x: 2 }}
                >
                  <path
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </motion.svg>
              </motion.button>
            </div>
          </motion.div>
        </div>
      </motion.section>
    );
  }

  // Desktop Layout
  return (
    <section
      className="relative py-32 overflow-hidden"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      <StarCanvas />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:14px_24px] opacity-30" />
      
      <div className="relative z-10 container mx-auto px-4">
        <motion.h2
          className="text-5xl md:text-6xl lg:text-7xl font-serif text-center mb-24 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/80"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          You'll Get
        </motion.h2>

        <div className="relative h-[600px] max-w-[1400px] mx-auto">
          {/* Cards */}
          <AnimatePresence mode="wait">
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                feature={feature}
                isActive={index === activeIndex}
                offset={index - activeIndex}
              />
            ))}
          </AnimatePresence>

          {/* Navigation buttons */}
          <button
            onClick={() => setActiveIndex((prev) => (prev - 1 + features.length) % features.length)}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all duration-300 group"
            aria-label="Previous slide"
          >
            <svg 
              className="w-6 h-6 text-white/50 group-hover:text-white/90 transition-colors"
              fill="none" 
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round" 
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={() => setActiveIndex((prev) => (prev + 1) % features.length)}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all duration-300 group"
            aria-label="Next slide"
          >
            <svg 
              className="w-6 h-6 text-white/50 group-hover:text-white/90 transition-colors"
              fill="none" 
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round" 
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Navigation dots */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex justify-center gap-2">
            {features.map((_, index) => (
              <button
                key={index}
                className={cn(
                  "w-1.5 h-1.5 rounded-full transition-all duration-300",
                  index === activeIndex
                    ? "w-6 bg-gradient-to-r from-purple-500 to-fuchsia-500"
                    : "bg-white/20 hover:bg-white/40"
                )}
                onClick={() => setActiveIndex(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureCards;

// Export StarField for use in other sections
export { StarField };